import { collection, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { bindConfigurationField } from './dataFieldBinding';
import { findDataFieldCandidate, readDataFieldRegistration, writeDataFieldRegistration } from './dataFieldRegistration';

const words = name => name.replace(/[^a-zA-Z0-9\s]/g, '').split(/\s+/).filter(Boolean);
const camelCase = name => words(name).map((word, index) => index
  ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase()).join('');
const snakeCase = name => words(name).map(word => word.toLowerCase()).join('_');
const comparable = ({ createdAt, updatedAt, ...data }) => data;

// Allocate once per open form, outside the transaction callback. Failed and
// uncertain-response retries keep these identities, even if inputs are edited.
export function newLinkedCreationAttempt(db) {
  return {
    refs: {
      prompt: doc(collection(db, 'prompts')),
      step: doc(collection(db, 'documentSteps')),
      section: doc(collection(db, 'documentSections')),
    },
    savedAt: new Date().toISOString(),
  };
}

export async function createLinkedConfiguration(db, attempt, options) {
  const {
    name, userId, prompt, promptId, processingType = 'none', sectionType = 'none',
    fieldSchema, createDataField = false, fieldCategory,
  } = options;
  const baseName = name.trim();
  const stepName = camelCase(baseName);
  const fieldName = snakeCase(baseName);
  const field = createDataField ? {
    fieldName, displayName: baseName, category: fieldCategory,
    showInHistoryFields: false, createdBy: userId,
  } : null;
  const candidate = await findDataFieldCandidate(db, field);
  const metadata = {
    version: 1, publishedVersion: null,
    createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: userId,
  };
  const history = data => [{ version: 1, ...data, savedAt: attempt.savedAt, savedBy: userId }];
  const records = {};
  if (prompt) {
    records.prompt = {
      ...metadata, name: stepName, type: prompt.type, model: prompt.model,
      prompt: prompt.text.trim(), prePrompt: '', postPrompt: '', active: true,
      versions: history({ prompt: prompt.text.trim() }),
    };
  }
  if (processingType !== 'none') {
    const linkedPrompt = prompt ? attempt.refs.prompt.id : promptId;
    records.step = {
      ...metadata, name: stepName, displayName: baseName, type: processingType,
      data: 'transcription', priority: true, allowGrouping: false, isLocked: false,
      versions: history({ displayName: baseName, type: processingType }),
      ...(linkedPrompt ? { promptName: linkedPrompt } : {}),
      ...(createDataField ? { output: fieldName } : {}),
      ...(processingType === 'empty' && fieldSchema ? { fieldSchemaRef: fieldSchema.id } : {}),
    };
  }
  if (sectionType !== 'none') {
    records.section = {
      ...metadata, name: stepName + 'Section', title: baseName, type: sectionType,
      defaultOpen: true, showHeader: true, sidebar: true, displayHistory: false,
      exports: ['doctor'], isLocked: false,
      versions: history({ title: baseName, type: sectionType }),
      ...(createDataField ? { dataField: fieldName } : {}),
      ...(fieldSchema ? { fieldSchemaRef: fieldSchema.id, fields: fieldSchema.name } : {}),
      ...(sectionType === 'EditableTableSection' ? {
        primaryField: 'name', secondaryField: 'summary', editorStyle: 'inline', tableColumns: 2,
      } : {}),
    };
  }

  await runTransaction(db, async transaction => {
    // Read even the currently unselected identities. A retry with changed
    // selections must not silently create another group after an earlier commit.
    const existing = {};
    for (const [kind, ref] of Object.entries(attempt.refs)) {
      existing[kind] = await transaction.get(ref);
    }
    const registration = await readDataFieldRegistration(transaction, db, field, candidate);
    const bound = { ...records };
    if (bound.step) bound.step = bindConfigurationField(bound.step, 'output', registration?.ref);
    if (bound.section) bound.section = bindConfigurationField(bound.section, 'dataField', registration?.ref);
    if (Object.values(existing).some(snapshot => snapshot.exists())) {
      const sameAttempt = Object.keys(existing).every(kind => bound[kind]
        ? existing[kind].exists() && isEqual(comparable(existing[kind].data()), comparable(bound[kind]))
        : !existing[kind].exists());
      if (sameAttempt) return;
      throw new Error('An item with this ID already exists');
    }
    for (const [kind, data] of Object.entries(bound)) transaction.set(attempt.refs[kind], data);
    writeDataFieldRegistration(transaction, registration, field);
  });
  return Object.keys(records).map(kind => kind[0].toUpperCase() + kind.slice(1))
    .concat(createDataField ? ['Data Field'] : []);
}
